import { useEffect, useMemo, useState } from 'react';
import {
  applyConsentPreferences,
  createConsentPreferences,
  getStoredConsent,
  saveConsent,
  type ConsentPreferences,
} from '../utils/consent';

declare global {
  interface Window {
    regaiConsentManager?: {
      open: () => void;
    };
  }
}

type ToggleState = {
  analytics: boolean;
  marketing: boolean;
};

const createToggleState = (prefs: ConsentPreferences | null): ToggleState => ({
  analytics: prefs?.analytics ?? false,
  marketing: prefs?.marketing ?? false,
});

export function ConsentBanner() {
  const storedPrefs = useMemo(() => getStoredConsent(), []);
  const [isOpen, setIsOpen] = useState(!storedPrefs);
  const [toggles, setToggles] = useState<ToggleState>(() => createToggleState(storedPrefs));

  const persist = (prefs: ConsentPreferences) => {
    saveConsent(prefs);
    applyConsentPreferences(prefs);
    setIsOpen(false);
  };

  const handleAcceptAll = () => {
    const prefs = createConsentPreferences({ analytics: true, marketing: true });
    persist(prefs);
  };

  const handleRejectAll = () => {
    const prefs = createConsentPreferences({ analytics: false, marketing: false });
    persist(prefs);
  };

  const handleSave = () => {
    const prefs = createConsentPreferences({
      analytics: toggles.analytics,
      marketing: toggles.marketing,
    });
    persist(prefs);
  };

  useEffect(() => {
    const reopen = () => {
      const latest = getStoredConsent();
      setToggles(createToggleState(latest));
      setIsOpen(true);
    };
    window.regaiConsentManager = { open: reopen };
    return () => {
      if (window.regaiConsentManager?.open === reopen) {
        delete window.regaiConsentManager;
      }
    };
  }, []);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 flex justify-center px-4 pb-6">
      <div className="w-full max-w-3xl rounded-2xl border border-slate-700 bg-slate-950/95 p-6 shadow-2xl backdrop-blur">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="md:max-w-[60%]">
            <h2 className="text-lg font-semibold text-slate-100">Impostazioni cookie</h2>
            <p className="mt-2 text-sm text-slate-300">
              Utilizziamo cookie e tecnologie simili per finalità di statistica (analytics) e marketing.
              Puoi accettare tutto, rifiutare o personalizzare le preferenze. I cookie tecnici necessari
              restano sempre attivi.
            </p>
            <div className="mt-4 space-y-3">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={toggles.analytics}
                  onChange={(event) => setToggles((prev) => ({ ...prev, analytics: event.target.checked }))}
                  className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-900 text-indigo-500 focus:ring-indigo-400"
                />
                <span>
                  <span className="block font-medium text-slate-100">Analytics</span>
                  <span className="text-sm text-slate-400">
                    Misurazione anonima del traffico con Google Analytics (consent mode) per migliorare il sito.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={toggles.marketing}
                  onChange={(event) => setToggles((prev) => ({ ...prev, marketing: event.target.checked }))}
                  className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-900 text-indigo-500 focus:ring-indigo-400"
                />
                <span>
                  <span className="block font-medium text-slate-100">Marketing</span>
                  <span className="text-sm text-slate-400">
                    Personalizzazione campagne e remarketing (es. Meta Pixel, Google Ads Conversioni).
                  </span>
                </span>
              </label>
            </div>
          </div>
          <div className="flex w-full flex-col gap-2 md:w-48">
            <button
              type="button"
              onClick={handleAcceptAll}
              className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:ring-offset-2 focus:ring-offset-slate-900"
            >
              Accetta tutto
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="rounded-lg border border-indigo-400 px-4 py-2 text-sm font-semibold text-indigo-200 hover:bg-indigo-500/10 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:ring-offset-2 focus:ring-offset-slate-900"
            >
              Salva preferenze
            </button>
            <button
              type="button"
              onClick={handleRejectAll}
              className="rounded-lg border border-transparent px-4 py-2 text-sm font-medium text-slate-300 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 focus:ring-offset-slate-900"
            >
              Rifiuta non necessari
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
